'use client';

import { useTranslations } from 'next-intl';
import {
    ComponentPropsWithoutRef,
    ComponentPropsWithRef,
    KeyboardEvent,
    ReactNode,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { AddressSuggestion } from '@/features/address/api/addresses';
import { cn } from '@/shared/lib/cn';
import { formatAddress } from '@/shared/lib/formatAddress';
import { AddressInput } from '@/shared/model/Address';
import { Field } from '@/shared/ui/fields';
import { Icon } from '@/shared/ui/icon';
import '@/shared/ui/fields/field.scss';

import { FormFieldState } from '@/shared/ui/form/types';
import { Popover } from '@/shared/ui/popover';
import './address-autocomplete-field.scss';

type Props = Omit<ComponentPropsWithRef<'input'>, 'onChange' | 'value'> & {
    getCurrentPosition: () => void;
    onChange?: (value: AddressInput | Record<string, never> | null) => void;
    textValue?: string;
    onTextValueChange?: (value: string) => void;
    state?: FormFieldState;
    fieldProps?: ComponentPropsWithoutRef<typeof Field>;
    suggestions?: AddressSuggestion[];
    isLoading?: boolean;
    isCurrentPositionUsed?: boolean;
    setIsCurrentPositionUsed?: (value: boolean) => void;
    additionalButtons?: ReactNode;
};
export const AddressAutocompleteField = ({
    getCurrentPosition,
    onChange,
    textValue,
    onTextValueChange,
    state,
    fieldProps,
    suggestions = [],
    isLoading,
    isCurrentPositionUsed = false,
    setIsCurrentPositionUsed,
    additionalButtons,
}: Props) => {
    const t = useTranslations();
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const [isOpen, setOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState<number>(0);

    const handleSelectAddress = () => {
        const focusedAddress: AddressInput | null =
            suggestions.length > 0
                ? {
                      city: suggestions[focusedIndex].city,
                      country: suggestions[focusedIndex].country,
                      postCode: suggestions[focusedIndex].postCode,
                      street: suggestions[focusedIndex].street,
                      lat: String(suggestions[focusedIndex].lat),
                      lon: String(suggestions[focusedIndex].lon),
                  }
                : null;
        if (focusedAddress) {
            onChange?.(focusedAddress);
        } else {
            onChange?.({});
        }
        setOpen(false);
    };
    const handleKeydown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((prev) => {
                return Math.min(prev + 1, suggestions.length - 1);
            });
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prev) => {
                return Math.max(prev - 1, 0);
            });
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSelectAddress();
        }
    };
    const updateSuggestionsHeight = () => {
        if (!suggestionsRef.current) {
            return;
        }
        suggestionsRef.current.style.setProperty(
            '--suggestions-height',
            `${suggestionsRef.current.scrollHeight}px`,
        );
    };

    useLayoutEffect(() => {
        updateSuggestionsHeight();
    }, [suggestions]);

    return (
        <Popover open={isOpen} onOpenChange={setOpen}>
            <Popover.Trigger autoFocus={false} asChild>
                <Field
                    fieldsetProps={{
                        className: cn(
                            isOpen && 'address-autocomplete-field__input--open',
                            state && `address-autocomplete-field__input--${state}`,
                        ),
                    }}
                    right={
                        <>
                            {state === 'error' && (
                                <Icon name="error" className="address-autocomplete-field__error-icon" />
                            )}
                            <button
                                type="button"
                                className={cn(
                                    'address-autocomplete-field__current-position-button',
                                    isCurrentPositionUsed &&
                                        'address-autocomplete-field__current-position-button--used',
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsCurrentPositionUsed?.(true);
                                    getCurrentPosition();
                                }}
                            >
                                <Icon name="current-location" />
                            </button>
                        </>
                    }
                    {...fieldProps}
                >
                    <input
                        type="text"
                        value={textValue}
                        placeholder={t('DELIVERY_SELECT.INPUT_PLACEHOLDER')}
                        onFocus={() => setOpen(true)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                            setOpen(true);
                            onTextValueChange?.(e.currentTarget.value);
                            setIsCurrentPositionUsed?.(false);
                        }}
                        onKeyDown={handleKeydown}
                        data-testid="address-autocomplete-field-input"
                        className="field__input"
                    />
                </Field>
            </Popover.Trigger>
            <Popover.Content
                sideOffset={0}
                autoFocus={false}
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                }}
                className={cn(
                    'address-autocomplete-field__suggestions',
                    isOpen && 'address-autocomplete-field__suggestions--open',
                )}
                onKeyDown={handleKeydown}
                avoidCollisions={false}
            >
                {isLoading && (
                    <div className="address-autocomplete-field__loading">
                        {/* You can add a spinner here if you have one */}
                        {t('WORDS.LOADING')}...
                    </div>
                )}
                {!isLoading && suggestions.length === 0 && (
                    <div className="address-autocomplete-field__empty">{t('DELIVERY_SELECT.NO_RESULTS')}</div>
                )}
                {!isLoading &&
                    suggestions.map(({ city, country, street, postCode, lat, lon, id }, index) => (
                        <button
                            key={index}
                            className={cn(
                                'address-autocomplete-field__suggestion',
                                focusedIndex === index && 'address-autocomplete-field__suggestion--focused',
                            )}
                            onMouseEnter={() => {
                                setFocusedIndex(index);
                            }}
                            onClick={handleSelectAddress}
                            data-testid="address-autocomplete-field-suggestion"
                        >
                            <Icon name="map-pin" />
                            {formatAddress({
                                id,
                                city,
                                country,
                                street,
                                postCode,
                                lat: `${lat}`,
                                lon: `${lon}`,
                            })}
                        </button>
                    ))}
                {additionalButtons}
            </Popover.Content>
        </Popover>
    );
};
